import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { BookOpen, Download, FileSpreadsheet, FileText, FileCode, Settings, FileDown } from 'lucide-react';

function ContabilidadLibros({ user }) {
  const navigate = useNavigate();
  const [tipoLibro, setTipoLibro] = useState('emitidas');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    // Set default dates to current year
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), 0, 1);
    const lastDay = new Date(now.getFullYear(), 11, 31);
    
    setFechaInicio(firstDay.toISOString().split('T')[0]);
    setFechaFin(lastDay.toISOString().split('T')[0]);
  }, []);

  const handleExport = async (formato) => {
    if (!fechaInicio || !fechaFin) {
      toast.error('Debes seleccionar un rango de fechas');
      return;
    }

    setExporting(true);
    try {
      const response = await axiosInstance.get('/contabilidad/libros/export', {
        params: {
          tipo: tipoLibro,
          formato: formato,
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin
        },
        responseType: 'blob'
      });

      // Crear un enlace temporal para descargar el archivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extraer nombre de archivo del header Content-Disposition
      const contentDisposition = response.headers['content-disposition'];
      let filename = `libro_facturas_${tipoLibro}_${fechaInicio}_${fechaFin}.${formato}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Libro exportado correctamente en formato ${formato.toUpperCase()}`);
    } catch (error) {
      console.error('Error exportando libro:', error);
      toast.error('Error al exportar el libro');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="p-6 max-w-4xl">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-blue-600" />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900">
                Contabilidad C10
              </h1>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/contabilidad/fiscal')}
                variant="outline"
                size="sm"
              >
                Dashboard
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/asientos')}
                variant="outline"
                size="sm"
              >
                Asientos
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/facturas-recibidas')}
                variant="outline"
                size="sm"
              >
                Fact. Recibidas
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/ocr')}
                variant="outline"
                size="sm"
              >
                OCR
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/config')}
                variant="outline"
                size="sm"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <p className="text-base text-gray-600">
            Libros Oficiales - Exportación en formatos oficiales (Excel, CSV, XML)
          </p>
        </div>

        <div className="space-y-6">
          {/* Selección de Libro */}
          <Card>
            <CardHeader>
              <CardTitle>Tipo de Libro</CardTitle>
              <CardDescription>
                Selecciona el libro que deseas exportar
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setTipoLibro('emitidas')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    tipoLibro === 'emitidas'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-lg font-semibold">Facturas Emitidas</div>
                    <div className="text-sm text-gray-500 mt-1">
                      Facturas emitidas a clientes
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setTipoLibro('recibidas')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    tipoLibro === 'recibidas'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-lg font-semibold">Facturas Recibidas</div>
                    <div className="text-sm text-gray-500 mt-1">
                      Facturas recibidas de proveedores
                    </div>
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Rango de Fechas */}
          <Card>
            <CardHeader>
              <CardTitle>Periodo</CardTitle>
              <CardDescription>
                Selecciona el rango de fechas para el libro
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fecha_inicio">Fecha Inicio</Label>
                  <Input
                    id="fecha_inicio"
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="fecha_fin">Fecha Fin</Label>
                  <Input
                    id="fecha_fin"
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const now = new Date();
                    const firstDay = new Date(now.getFullYear(), 0, 1);
                    const lastDay = new Date(now.getFullYear(), 11, 31);
                    setFechaInicio(firstDay.toISOString().split('T')[0]);
                    setFechaFin(lastDay.toISOString().split('T')[0]);
                  }}
                >
                  Año Actual
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const now = new Date();
                    const quarter = Math.floor(now.getMonth() / 3);
                    const firstMonth = quarter * 3;
                    const firstDay = new Date(now.getFullYear(), firstMonth, 1);
                    const lastDay = new Date(now.getFullYear(), firstMonth + 3, 0);
                    setFechaInicio(firstDay.toISOString().split('T')[0]);
                    setFechaFin(lastDay.toISOString().split('T')[0]);
                  }}
                >
                  Trimestre Actual
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const now = new Date();
                    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                    setFechaInicio(firstDay.toISOString().split('T')[0]);
                    setFechaFin(lastDay.toISOString().split('T')[0]);
                  }}
                >
                  Mes Actual
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Formatos de Exportación */}
          <Card>
            <CardHeader>
              <CardTitle>Exportar Libro</CardTitle>
              <CardDescription>
                Descarga el libro en el formato que necesites
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <Button
                  onClick={() => handleExport('excel')}
                  disabled={exporting}
                  className="h-24 flex flex-col items-center justify-center gap-2"
                  variant="outline"
                >
                  <FileSpreadsheet className="h-8 w-8 text-green-600" />
                  <span className="font-semibold">Excel</span>
                  <span className="text-xs text-gray-500">.xlsx</span>
                </Button>
                <Button
                  onClick={() => handleExport('csv')}
                  disabled={exporting}
                  className="h-24 flex flex-col items-center justify-center gap-2"
                  variant="outline"
                >
                  <FileText className="h-8 w-8 text-blue-600" />
                  <span className="font-semibold">CSV</span>
                  <span className="text-xs text-gray-500">.csv</span>
                </Button>
                <Button
                  onClick={() => handleExport('xml')}
                  disabled={exporting}
                  className="h-24 flex flex-col items-center justify-center gap-2"
                  variant="outline"
                >
                  <FileCode className="h-8 w-8 text-orange-600" />
                  <span className="font-semibold">XML</span>
                  <span className="text-xs text-gray-500">.xml</span>
                </Button>
              </div>

              {exporting && (
                <div className="mt-4 text-center text-gray-500">
                  <Download className="h-6 w-6 animate-bounce mx-auto mb-2" />
                  Generando archivo...
                </div>
              )}
            </CardContent>
          </Card>

          {/* Información */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="text-sm text-blue-800 space-y-2">
                <p><strong>Formatos disponibles:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><strong>Excel (.xlsx):</strong> Formato ideal para análisis y presentación, con formato y colores</li>
                  <li><strong>CSV (.csv):</strong> Formato compatible con cualquier aplicación de hoja de cálculo</li>
                  <li><strong>XML (.xml):</strong> Formato estructurado para sistemas de contabilidad e intercambio de datos</li>
                </ul>
                <p className="mt-3">
                  Los libros incluyen: Número de factura, fecha, NIF/CIF, nombre, base imponible, IVA y total.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

export default ContabilidadLibros;
